import { queryClient } from '@/lib/api-client'
import { nativeQueueManager } from '@/lib/nativeSync/nativeQueueManager'
import { isNativePlatform } from '@/lib/platform/platformDetection'
import { MIN_REFRESH_DURATION } from './pullToRefreshConstants'

export async function triggerRefresh(): Promise<void> {
  const minDuration = new Promise<void>(resolve => setTimeout(resolve, MIN_REFRESH_DURATION))

  // On iOS/Android native, the prefetch system caches 500+ individual issue
  // queries (singleIssue, singleIssueComments, singleIssueEvents).
  // invalidateQueries with a prefix key matches ALL of them, triggering 1500+
  // parallel refetches + PersistQueryClientProvider dehydration storms → OOM.
  //
  // What refetchType:'none' below DOES fix: the refetch fan-out. Per-issue
  // caches are only marked stale and refetch lazily when the user opens them,
  // while the few small list/summary queries refetch actively.
  //
  // What it does NOT fix — do not read the above as "storm solved":
  // query-core marks EVERY match before it looks at refetchType
  // (queryClient.ts:298-304), so all 500+ still dispatch an 'updated' cache
  // event. Those reach the persister, and persistQueryClientSave() builds
  // clientState: dehydrate(...) BEFORE calling persistClient (persist.ts:119-125),
  // which is where our pausePersist() check lives (queryPersister.ts:146) — so
  // pausing suppresses the IDB write, not the dehydrate. The real cost of that
  // is UNMEASURED (query.ts:380-384 skips already-invalidated queries, and the
  // block is inside notifyManager.batch, both of which bound it). Measure on
  // WKWebView before changing this.
  //
  // Note the web/native asymmetry is smaller than it looks: refetchType:'active'
  // is query-core's default (queryClient.ts:308) and only refetches queries with
  // an active observer, so web's fan-out is the mounted page, not 500 queries.
  //
  // A second live site does the same unbounded marking with no native handling
  // at all: useQueueDrainInvalidator.ts:49.
  const isNative = isNativePlatform()

  const invalidate = Promise.all([
    // List and summary queries — always actively refetch (few queries, small payloads)
    queryClient.invalidateQueries({ queryKey: ['stageIssuesByIssueType'] }),
    queryClient.invalidateQueries({ queryKey: ['currStageIssueTypes'] }),
    queryClient.invalidateQueries({ queryKey: ['currStageUsersQuery'] }),
    queryClient.invalidateQueries({ queryKey: ['getIssuesSummary'] }),
    queryClient.invalidateQueries({ queryKey: ['getNotificationsSummary'] }),

    // Per-issue caches — on native, mark stale without mass refetch.
    // On web, refetch immediately (cache is smaller, no WKWebView constraint).
    queryClient.invalidateQueries({
      queryKey: ['singleIssue'],
      refetchType: isNative ? 'none' : 'active',
    }),
    queryClient.invalidateQueries({
      queryKey: ['singleIssueComments'],
      refetchType: isNative ? 'none' : 'active',
    }),
    queryClient.invalidateQueries({
      queryKey: ['singleIssueEvents'],
      refetchType: isNative ? 'none' : 'active',
    }),
  ])

  const tasks: Promise<unknown>[] = [invalidate, minDuration]
  tasks.push(nativeQueueManager.performSync())

  await Promise.allSettled(tasks)
}
